import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";

/** Paso del recorrido: señala el elemento con `data-tour="<target>"` (o se centra si no hay). */
export interface TourStep {
  target?: string;
  title: string;
  body: ReactNode;
}

interface TourApi {
  start: (steps: TourStep[]) => void;
  active: boolean;
}

const TourContext = createContext<TourApi>({ start: () => {}, active: false });
export const useTour = () => useContext(TourContext);

const PAD = 8;
const POPOVER_W = 360;
const GAP = 14;

const findTarget = (target?: string) => (target ? document.querySelector<HTMLElement>(`[data-tour="${target}"]`) : null);

/**
 * Recorrido guiado sin dependencias: oscurece la pantalla, resalta un elemento y muestra una
 * explicación junto a él. Los pasos cuyo elemento no existe en la pantalla actual se omiten
 * (así un mismo recorrido sirve para estudios de imágenes y de video).
 */
export function TourProvider({ children }: { children: ReactNode }) {
  const [steps, setSteps] = useState<TourStep[] | null>(null);
  const [index, setIndex] = useState(0);
  const start = useCallback((all: TourStep[]) => {
    const available = all.filter((s) => !s.target || findTarget(s.target));
    if (available.length === 0) return;
    setIndex(0);
    setSteps(available);
  }, []);
  const close = useCallback(() => setSteps(null), []);
  return (
    <TourContext.Provider value={{ start, active: steps !== null }}>
      {children}
      {steps && <TourOverlay steps={steps} index={index} onIndex={setIndex} onClose={close} />}
    </TourContext.Provider>
  );
}

function TourOverlay({ steps, index, onIndex, onClose }: { steps: TourStep[]; index: number; onIndex: (i: number) => void; onClose: () => void }) {
  const step = steps[index]!;
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight });
  const nextRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const last = index === steps.length - 1;

  // Llevar el elemento a la vista y seguir su posición (desplazamiento, cambios de tamaño).
  useLayoutEffect(() => {
    const el = findTarget(step.target);
    if (!el) {
      setRect(null);
      return;
    }
    el.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
    let raf = 0;
    const track = () => {
      setRect(el.getBoundingClientRect());
      setVp({ w: window.innerWidth, h: window.innerHeight });
      raf = requestAnimationFrame(track);
    };
    raf = requestAnimationFrame(track);
    return () => cancelAnimationFrame(raf);
  }, [step.target]);

  useEffect(() => {
    nextRef.current?.focus();
  }, [index]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && !last) onIndex(index + 1);
      else if (e.key === "ArrowLeft" && index > 0) onIndex(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, last, onClose, onIndex]);

  // Posición del cuadro: debajo del elemento si cabe; si no, arriba; si tampoco, a un lado.
  const w = Math.min(POPOVER_W, vp.w - 24);
  let pos: { left: number; top: number } | null = null;
  if (rect) {
    const estH = 220;
    const left = Math.min(Math.max(12, rect.left + rect.width / 2 - w / 2), vp.w - w - 12);
    if (rect.bottom + GAP + estH < vp.h) pos = { left, top: rect.bottom + PAD + GAP };
    else if (rect.top - GAP - estH > 0) pos = { left, top: rect.top - PAD - GAP - estH };
    else if (rect.right + GAP + w < vp.w) pos = { left: rect.right + PAD + GAP, top: Math.max(12, Math.min(rect.top, vp.h - estH - 12)) };
    else pos = { left: Math.max(12, rect.left - PAD - GAP - w), top: Math.max(12, Math.min(rect.top, vp.h - estH - 12)) };
  }

  return (
    <div className="fixed inset-0 z-[80]" data-testid="tour">
      {/* Capa que bloquea la interacción con la página durante el recorrido. */}
      <div className="absolute inset-0" onClick={onClose} aria-hidden />
      {rect ? (
        <div
          aria-hidden
          className="pointer-events-none absolute rounded-xl ring-2 ring-aqua transition-all duration-200"
          style={{
            left: rect.left - PAD,
            top: rect.top - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: "0 0 0 9999px rgba(6, 26, 33, 0.58)",
          }}
        />
      ) : (
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-[rgba(6,26,33,0.58)]" />
      )}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="absolute rounded-2xl bg-white p-4 shadow-2xl ring-1 ring-slate-200"
        style={pos ? { left: pos.left, top: pos.top, width: w } : { left: "50%", top: "50%", width: w, transform: "translate(-50%, -50%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-util text-[0.7rem] uppercase tracking-[0.2em] text-slate-500">
          Recorrido · {index + 1} / {steps.length}
        </p>
        <h2 id={titleId} className="mt-1 text-base font-bold text-ink">
          {step.title}
        </h2>
        <div className="mt-1.5 space-y-1.5 text-sm text-slate-700">{step.body}</div>
        <div className="mt-4 flex items-center gap-2">
          <button type="button" className="text-sm font-medium text-slate-500 hover:text-slate-800" onClick={onClose} data-testid="tour-close">
            Salir
          </button>
          <div className="ml-auto flex gap-2">
            {index > 0 && (
              <button type="button" className="btn-secondary px-3 py-1.5 text-sm" onClick={() => onIndex(index - 1)}>
                Anterior
              </button>
            )}
            <button ref={nextRef} type="button" className="btn-primary px-3 py-1.5 text-sm" onClick={() => (last ? onClose() : onIndex(index + 1))} data-testid="tour-next">
              {last ? "Terminar" : "Siguiente"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
